import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useThemeMode } from "../components/ThemeMode";
import { COMPANY, SiteFooter, SiteNav } from "../components/site/SiteChrome";
import "./LandingPage.css";

// The privacy policy (public, /privacy): the website, the Testify app and the Testify
// Recorder Chrome extension. It describes what the code actually does; keep it in step
// when data handling changes (new processors, new stored data, the extension).
const UPDATED = "26 October 2026";

const SECTIONS = [
	["who", "Who we are"],
	["collect", "What we collect"],
	["extension", "The Testify Recorder extension"],
	["use", "How we use it"],
	["share", "Who we share it with"],
	["security", "How we protect it"],
	["storage", "Cookies and browser storage"],
	["retention", "How long we keep it"],
	["rights", "Your choices and rights"],
	["children", "Children"],
	["changes", "Changes to this policy"],
	["contact", "Contact us"],
];

const Section = ({ id, title, children }) => (
	<section id={id} className="legal-section">
		<h2>{title}</h2>
		{children}
	</section>
);

export default function Privacy() {
	const { mode } = useThemeMode();

	useEffect(() => {
		document.title = "Privacy Policy — Testify";
		window.scrollTo(0, 0);
		return () => {
			document.title = "Testify";
		};
	}, []);

	const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>;

	return (
		<div className="lp" data-theme={mode}>
			<SiteNav />

			<header className="hero contact-hero">
				<div className="hero-bg" aria-hidden="true">
					<div className="blob blob-1" />
					<div className="blob blob-2" />
					<div className="hero-grid" />
				</div>
				<div className="container contact-head">
					<span className="eyebrow">Legal</span>
					<h1>
						Privacy <span className="grad-text">Policy</span>
					</h1>
					<p className="hero-lead">
						What we collect when you use Testify, why, and what you can do about it — in plain words.
					</p>
					<p className="legal-updated">Last updated {UPDATED}</p>
				</div>
			</header>

			<section className="legal-wrap">
				<div className="container legal-grid">
					<nav className="legal-toc" aria-label="On this page">
						<span className="legal-toc-title">On this page</span>
						{SECTIONS.map(([id, title]) => (
							<a key={id} href={`#${id}`}>
								{title}
							</a>
						))}
					</nav>

					<article className="legal">
						<Section id="who" title="Who we are">
							<p>
								Testify is made by Saarka Tech ("we", "us"), {COMPANY.address.join(" ")}. This policy covers the Testify
								website (testify.saarkatech.com), the Testify web application, and the Testify Recorder extension for
								Google Chrome.
							</p>
							<p>
								Testify is used by organisations. Your organisation decides who gets an account and what is tested; for the
								test data your organisation puts into Testify, we act on its behalf. If your organisation runs Testify on its
								own servers, it controls that data and its own policy applies to it.
							</p>
						</Section>

						<Section id="collect" title="What we collect">
							<h3>When you contact us</h3>
							<p>
								The demo request and contact forms ask for your name, email address, company, and optionally your team size,
								phone number and a message. We send what you submit to our team by email; it isn't stored in the Testify
								database. We briefly use your IP address to stop the forms being abused.
							</p>
							<h3>Your account</h3>
							<p>
								Your name, email address, role and organisation. Your password is stored only as a one-way hash; we can't
								read it. If you reset your password, we store a single-use link that expires after 30 minutes.
							</p>
							<h3>What you create in Testify</h3>
							<ul>
								<li>
									Projects, folders, tests, suites, data sets and schedules. A recorded test holds the pages you visited while
									recording, details of the elements you used (such as their text, labels and attributes), and the values you
									typed — which can include passwords typed into the website you recorded.
								</li>
								<li>
									API tests: the requests you define and the responses they received. Sensitive headers such as
									authorization, cookies and API keys are masked in stored results.
								</li>
								<li>
									Run results: each step's outcome, error messages and screenshots of the tested website, visual comparison
									images, accessibility findings and self-healing suggestions.
								</li>
								<li>
									API tokens for CI/CD pipelines (only a hash is stored; the token is shown to you once), notification
									settings (email addresses and Slack or Microsoft Teams webhook addresses) and report share links.
								</li>
							</ul>
							<h3>Technical information</h3>
							<p>
								Like any website, our servers keep standard request logs, such as IP address, time and the page or address
								requested, to run and secure the service.
							</p>
						</Section>

						<Section id="extension" title="The Testify Recorder extension">
							<p>The extension records tests for Testify. It only works for people signed in to Testify.</p>
							<ul>
								<li>
									<strong>When it records:</strong> only after you start a recording in the Testify app, and only in the
									browser window Testify opens for it. It does nothing in your other tabs and windows.
								</li>
								<li>
									<strong>What it records:</strong> your clicks, typing, selections, scrolling and drag-and-drop in that
									window, the addresses of the pages you open there, details of the elements you use, and links the page
									copies (so a pasted link can be replayed). Anything you type while recording, including passwords, becomes
									part of the test.
								</li>
								<li>
									<strong>Where it goes:</strong> recorded steps are sent only to the Testify page that started the recording.
									The extension keeps them in memory, never on disk, and doesn't send them anywhere else. They are stored in
									your organisation's Testify account when you save the test.
								</li>
								<li>
									<strong>Sign-in check:</strong> before it starts, the extension sends your Testify sign-in token to Testify
									to confirm you're signed in.
								</li>
								<li>
									<strong>Replaying steps:</strong> when you edit a test, the extension can replay its earlier steps in the
									tab it opened, using Chrome's debugging interface for that tab only. Chrome shows a notice while this
									happens.
								</li>
								<li>
									<strong>What it doesn't do:</strong> it has no analytics or advertising, doesn't track your browsing, and
									doesn't sell or share your data.
								</li>
							</ul>
							<p>
								The use of information received from the extension adheres to the{" "}
								<a href="https://developer.chrome.com/docs/webstore/program-policies/policies" target="_blank" rel="noopener noreferrer">
									Chrome Web Store User Data Policy
								</a>
								, including the Limited Use requirements.
							</p>
						</Section>

						<Section id="use" title="How we use it">
							<ul>
								<li>To provide Testify: record, store and run your tests, and show you their results.</li>
								<li>To send the emails you ask for: password resets, invitations and run notifications.</li>
								<li>To keep Testify secure, prevent abuse and fix problems.</li>
								<li>To answer your questions and demo requests.</li>
							</ul>
							<p>We don't sell your data, use it for advertising, or build profiles of you.</p>
						</Section>

						<Section id="share" title="Who we share it with">
							<p>We share data only with the services we use to run Testify, and only what they need:</p>
							<ul>
								<li>
									<strong>Groq</strong> (AI, United States), for self-healing and failure analysis: details of the element a
									test was looking for, a trimmed part of the page's HTML, selectors, test names and error messages. When a
									run fails, the failed test's steps too (their selectors, element text, page addresses and typed values;
									typed passwords are left out).
								</li>
								<li>
									<strong>Microsoft 365</strong>, to send email.
								</li>
								<li>
									<strong>Google Fonts</strong>: your browser loads the site's fonts from Google, which receives your IP
									address.
								</li>
								<li>
									<strong>Slack and Microsoft Teams</strong>, only if your organisation sets up run notifications: a summary
									of each run is posted to the channel it chose.
								</li>
								<li>
									<strong>People you share a report with:</strong> anyone with a report link can read that run's report,
									including its screenshots, until the link expires or is revoked.
								</li>
							</ul>
							<p>We may also disclose information when the law requires it.</p>
						</Section>

						<Section id="security" title="How we protect it">
							<p>
								Testify is served over HTTPS. Each organisation's data is kept in its own database, and roles decide who can
								see and change what. Passwords and API tokens are stored only as hashes. Because recorded tests and
								screenshots can contain what was typed on the tested website, we recommend recording with test accounts rather
								than real ones.
							</p>
						</Section>

						<Section id="storage" title="Cookies and browser storage">
							<p>
								We don't use cookies for tracking or advertising. The app keeps your sign-in token, your light or dark theme
								choice and your progress through the Help checklist in your browser's local storage.
							</p>
						</Section>

						<Section id="retention" title="How long we keep it">
							<p>
								We keep your organisation's data while it uses Testify. You can delete tests and other items at any time. To
								have your account or all of your organisation's data deleted, email us at {mail}. Messages from the demo and
								contact forms stay in our email.
							</p>
						</Section>

						<Section id="rights" title="Your choices and rights">
							<p>
								You can ask us for a copy of your personal information, or to correct or delete it, by emailing {mail}.
								Depending on where you live you may have further rights, and you can complain to your data protection
								authority. Your organisation's administrators manage accounts and can also help.
							</p>
						</Section>

						<Section id="children" title="Children">
							<p>Testify is a tool for businesses and isn't meant for children.</p>
						</Section>

						<Section id="changes" title="Changes to this policy">
							<p>
								When we change this policy, we update the date at the top of this page. If a change matters, we'll tell your
								organisation's administrators.
							</p>
						</Section>

						<Section id="contact" title="Contact us">
							<p>
								Questions about privacy? Email {mail}, call <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>, or use our{" "}
								<Link to="/contact">contact page</Link>.
							</p>
							<address>
								Saarka Tech
								<br />
								{COMPANY.address.map((line) => (
									<span key={line}>
										{line}
										<br />
									</span>
								))}
							</address>
						</Section>
					</article>
				</div>
			</section>

			<SiteFooter />
		</div>
	);
}
